import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { workspacesApi, type WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import { boardColumnsApi, type BoardColumn } from '../boardColumns/boardColumnsApi';
import { labelsApi, type Label } from '../labels/labelsApi';
import { projectsApi, type Project, type ProjectStatus } from '../projects/projectsApi';
import { tasksApi, type Task, type UpdateTaskRequest } from '../tasks/tasksApi';
import { sprintsApi, type Sprint } from '../sprints/sprintsApi';
import { projectMembersApi, type ProjectMember } from '../projectMembers/projectMembersApi';
import { settingsApi, type WorkspaceSettings } from '../settings/settingsApi';
import TaskCard from '../tasks/TaskCard';
import TaskListRow from '../tasks/TaskListRow';
import TaskPanel, { type TaskPanelHandle } from '../components/TaskPanel';
import RightPanel from '../components/RightPanel';
import RichTextEditor, { RichTextContent } from '../components/RichTextEditor';
import CalendarView from '../components/CalendarView';
import { DeleteButton } from '../components/DeleteButton';
import { PencilIcon } from '../components/icons';

const PROJECT_STATUSES: ProjectStatus[] = ['ACTIVE', 'DONE', 'ARCHIVED'];
const ALL_FILTER = 'ALL';

export default function ProjectDetailPage() {
  const { workspaceId = '', projectId = '' } = useParams<{ workspaceId: string; projectId: string }>();
  const { user, getToken, refresh } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState<ProjectStatus>('ACTIVE');
  const [saving, setSaving] = useState(false);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState('');

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [members, setMembers] = useState<WorkspaceMemberInfo[]>([]);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [settings, setSettings] = useState<WorkspaceSettings>({ workspaceId, pointsPerDay: 1, scheduleType: 'BUSINESS_DAYS', updatedAt: 0 });
  const [activeTab, setActiveTab] = useState<'tasks' | 'calendar' | 'members'>('tasks');
  const [taskViewMode, setTaskViewMode] = useState<'card' | 'list'>('list');

  // Filters
  const [assigneeFilter, setAssigneeFilter] = useState(ALL_FILTER);
  const [pointsFilter, setPointsFilter] = useState(ALL_FILTER);
  const [labelFilter, setLabelFilter] = useState(ALL_FILTER);

  // Right-panel mode: null = closed, 'create' = new task form, string taskId = task detail
  const [panelMode, setPanelMode] = useState<null | 'create' | string>(null);
  const createPanelRef = useRef<TaskPanelHandle>(null);
  const viewPanelRef = useRef<TaskPanelHandle>(null);

  const selectedTask = useMemo(
    () => (panelMode && panelMode !== 'create' ? (tasks.find(t => t.taskId === panelMode) ?? null) : null),
    [tasks, panelMode],
  );

  const [addMemberId, setAddMemberId] = useState('');
  const [addMemberCapacity, setAddMemberCapacity] = useState(1.0);
  const [addingMember, setAddingMember] = useState(false);

  const loadProject = useCallback(() => {
    setLoading(true);
    projectsApi
      .get(workspaceId, projectId, getToken, refresh)
      .then(data => {
        setProject(data);
        setEditName(data.name);
        setEditDescription(data.description);
        setEditStatus(data.status);
      })
      .catch(() => setError('Failed to load project'))
      .finally(() => setLoading(false));
  }, [workspaceId, projectId, getToken, refresh]);

  const loadTasks = useCallback(() => {
    setTasksLoading(true);
    tasksApi
      .list(workspaceId, projectId, getToken, refresh)
      .then(data => setTasks(data.tasks))
      .catch(() => setTasksError('Failed to load tasks'))
      .finally(() => setTasksLoading(false));
  }, [workspaceId, projectId, getToken, refresh]);

  const loadSprints = useCallback(() => {
    sprintsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setSprints(data.sprints))
      .catch(() => { /* sprint dropdown optional */ });
  }, [workspaceId, getToken, refresh]);

  const loadLabels = useCallback(() => {
    labelsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setLabels(data.labels))
      .catch(() => { /* optional */ });
  }, [workspaceId, getToken, refresh]);

  const loadColumns = useCallback(() => {
    boardColumnsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setColumns(data.columns))
      .catch(() => { /* optional */ });
  }, [workspaceId, getToken, refresh]);

  const loadMembers = useCallback(() => {
    workspacesApi
      .listMembers(workspaceId, getToken, refresh)
      .then(data => setMembers(data.members))
      .catch(() => { /* optional */ });
  }, [workspaceId, getToken, refresh]);

  const loadProjectMembers = useCallback(() => {
    projectMembersApi
      .list(workspaceId, projectId, getToken, refresh)
      .then(data => setProjectMembers(data.members))
      .catch(() => { /* optional */ });
  }, [workspaceId, projectId, getToken, refresh]);

  const loadSettings = useCallback(() => {
    settingsApi
      .get(workspaceId, getToken, refresh)
      .then(data => setSettings(data))
      .catch(() => { /* optional */ });
  }, [workspaceId, getToken, refresh]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadSprints(); }, [loadSprints]);
  useEffect(() => { loadColumns(); }, [loadColumns]);
  useEffect(() => { loadLabels(); }, [loadLabels]);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadProjectMembers(); }, [loadProjectMembers]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  useEffect(() => {
    if (panelMode && panelMode !== 'create' && !tasks.find(t => t.taskId === panelMode)) {
      setPanelMode(null);
    }
  }, [tasks, panelMode]);

  const pointOptions = useMemo(
    () => Array.from(new Set(tasks.map(t => t.points))).sort((a, b) => a - b),
    [tasks],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter(task => {
        if (assigneeFilter !== ALL_FILTER && !task.assigneeIds.includes(assigneeFilter)) return false;
        if (pointsFilter !== ALL_FILTER && task.points !== Number(pointsFilter)) return false;
        if (labelFilter !== ALL_FILTER && !task.labelIds.includes(labelFilter)) return false;
        return true;
      }),
    [tasks, assigneeFilter, pointsFilter, labelFilter],
  );

  async function handleAddMember(e: FormEvent) {
    e.preventDefault();
    if (!addMemberId) return;
    setAddingMember(true);
    try {
      await projectMembersApi.add(workspaceId, projectId, addMemberId, addMemberCapacity, getToken, refresh);
      setAddMemberId('');
      setAddMemberCapacity(1.0);
      loadProjectMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  }

  async function handleRemoveMember(userId: string) {
    try {
      await projectMembersApi.remove(workspaceId, projectId, userId, getToken, refresh);
      loadProjectMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!editName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await projectsApi.update(
        workspaceId,
        projectId,
        { name: editName.trim(), description: editDescription.trim(), status: editStatus },
        getToken,
        refresh,
      );
      setProject(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSaving(false);
    }
  }

  function startEdit() {
    if (!project) return;
    setEditName(project.name);
    setEditDescription(project.description);
    setEditStatus(project.status);
    setError('');
    setEditing(true);
  }

  async function handleDelete() {
    setError('');
    try {
      await projectsApi.remove(workspaceId, projectId, getToken, refresh);
      navigate(project ? `/workspaces/${workspaceId}/epics/${project.epicId}` : `/workspaces/${workspaceId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  }

  async function handleTaskUpdate(taskId: string, updates: UpdateTaskRequest) {
    const updated = await tasksApi.update(workspaceId, taskId, updates, getToken, refresh);
    setTasks(prev => prev.map(t => (t.taskId === taskId ? updated : t)));
    return updated;
  }

  async function handleTaskSprintChange(task: Task, sprintId: string) {
    setTasksError('');
    try {
      const updated = await tasksApi.update(
        workspaceId,
        task.taskId,
        { sprintId: sprintId || 'NONE' },
        getToken,
        refresh,
      );
      setTasks(prev => prev.map(t => (t.taskId === task.taskId ? updated : t)));
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'Failed to update task');
    }
  }

  async function handleTaskStatusChange(task: Task, status: string) {
    try {
      const updated = await tasksApi.update(workspaceId, task.taskId, { status }, getToken, refresh);
      setTasks(prev => prev.map(t => (t.taskId === task.taskId ? updated : t)));
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'Failed to update task');
    }
  }

  async function handleTaskDelete(task: Task) {
    setTasksError('');
    try {
      await tasksApi.remove(workspaceId, task.taskId, getToken, refresh);
      setTasks(prev => prev.filter(t => t.taskId !== task.taskId));
      if (panelMode === task.taskId) setPanelMode(null);
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  }

  function handleTaskCreated(task: Task) {
    setTasks(prev => [task, ...prev]);
    setPanelMode(task.taskId);
  }

  if (loading) {
    return (
      <main className="page">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="page">
        <p className="error">{error || 'Project not found'}</p>
      </main>
    );
  }

  return (
    <main className="page" style={{ marginRight: !isMobile && panelMode ? 456 : undefined }}>
      <p>
        <Link to={`/workspaces/${workspaceId}/epics/${project.epicId}`}>← Epic</Link>
      </p>

      {error && <p className="error">{error}</p>}

      {!editing ? (
        <div className="card">
          <div className="toolbar--split">
            <h1 style={{ margin: 0 }}>{project.name}</h1>
            <button type="button" className="icon-btn" title="Edit project" onClick={startEdit}>
              <PencilIcon />
            </button>
          </div>
          <p className="muted" style={{ margin: '4px 0' }}>{project.status}</p>
          {project.description && <RichTextContent html={project.description} />}
        </div>
      ) : (
        <form onSubmit={handleSave} className="form-card">
          <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" required />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
            <span>Description</span>
            <RichTextEditor value={editDescription} onChange={setEditDescription} />
          </div>
          <select value={editStatus} onChange={e => setEditStatus(e.target.value as ProjectStatus)}>
            {PROJECT_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <div className="form-actions toolbar--split">
            <div className="toolbar">
              <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            </div>
            <DeleteButton onConfirm={handleDelete} title="Delete project" />
          </div>
        </form>
      )}

      {/* Tab switcher */}
      <div className="toolbar" style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 16 }}>
        {([['tasks', 'Tasks'], ['calendar', 'Calendar'], ['members', 'Members']] as const).map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab as typeof activeTab)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: '8px 12px',
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: 14,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tasks tab */}
      {activeTab === 'tasks' && (
        <>
          {/* Toolbar */}
          <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>Tasks</h2>
            <div className="toolbar">
              <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => { setTaskViewMode('card'); setPanelMode(null); }}
                  title="Card view"
                  style={{
                    padding: '4px 10px',
                    fontSize: 13,
                    background: taskViewMode === 'card' ? 'var(--color-primary)' : 'transparent',
                    color: taskViewMode === 'card' ? '#fff' : 'var(--color-text-muted)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  ▦ Cards
                </button>
                <button
                  type="button"
                  onClick={() => setTaskViewMode('list')}
                  title="List view"
                  style={{
                    padding: '4px 10px',
                    fontSize: 13,
                    background: taskViewMode === 'list' ? 'var(--color-primary)' : 'transparent',
                    color: taskViewMode === 'list' ? '#fff' : 'var(--color-text-muted)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  ☰ List
                </button>
              </div>
              <button
                type="button"
                onClick={() => setPanelMode(prev => (prev === 'create' ? null : 'create'))}
                style={panelMode === 'create' ? { background: 'var(--color-primary)', color: '#fff' } : undefined}
              >
                + Create Task
              </button>
            </div>
          </div>

          {/* Filters (list mode only) */}
          {taskViewMode === 'list' && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 12px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                Assignee
                <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
                  <option value={ALL_FILTER}>All</option>
                  {members.map(m => (
                    <option key={m.userId} value={m.userId}>{m.displayName}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                Points
                <select value={pointsFilter} onChange={e => setPointsFilter(e.target.value)}>
                  <option value={ALL_FILTER}>All</option>
                  {pointOptions.map(pts => (
                    <option key={pts} value={pts}>{pts}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                Label
                <select value={labelFilter} onChange={e => setLabelFilter(e.target.value)}>
                  <option value={ALL_FILTER}>All</option>
                  {labels.map(label => (
                    <option key={label.labelId} value={label.labelId}>{label.name}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {tasksLoading && <p className="muted">Loading tasks…</p>}
          {tasksError && <p className="error">{tasksError}</p>}

          {taskViewMode === 'card' ? (
            <>
              {!tasksLoading && tasks.length === 0 && <p className="muted">No tasks yet.</p>}
              <ul className="card-list">
                {tasks.map(task => (
                  <li key={task.taskId}>
                    <TaskCard
                      task={task}
                      workspaceId={workspaceId}
                      columns={columns}
                      labels={labels}
                      members={members}
                      sprints={sprints}
                      onUpdate={handleTaskUpdate}
                      onSprintChange={sprintId => handleTaskSprintChange(task, sprintId)}
                      onDelete={() => handleTaskDelete(task)}
                    />
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
              {filteredTasks.map(task => (
                <TaskListRow
                  key={task.taskId}
                  task={task}
                  columns={columns}
                  labels={labels}
                  members={members}
                  selected={panelMode === task.taskId}
                  onSelect={() => setPanelMode(prev => (prev === task.taskId ? null : task.taskId))}
                  onStatusChange={status => handleTaskStatusChange(task, status)}
                />
              ))}
              {!tasksLoading && filteredTasks.length === 0 && (
                <p className="muted" style={{ padding: '12px 16px', margin: 0 }}>
                  {tasks.length === 0 ? 'No tasks yet.' : 'No tasks match the current filters.'}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Calendar tab */}
      {activeTab === 'calendar' && (
        <>
          <h2 style={{ margin: '0 0 16px' }}>Calendar</h2>
          <CalendarView
            tasks={tasks}
            pointsPerDay={settings.pointsPerDay}
            scheduleType={settings.scheduleType}
            onTaskClick={task => setPanelMode(task.taskId)}
            onSaveVelocity={async (v) => {
              const updated = await settingsApi.update(workspaceId, { pointsPerDay: v }, getToken, refresh);
              setSettings(updated);
            }}
            onSaveScheduleType={async (type) => {
              const updated = await settingsApi.update(workspaceId, { scheduleType: type }, getToken, refresh);
              setSettings(updated);
            }}
          />
        </>
      )}

      {/* Right panel — task create or task detail */}
      {panelMode === 'create' && user && (
        <RightPanel
          title="Create Task"
          onClose={async () => { await createPanelRef.current?.trySubmit(); setPanelMode(null); }}
        >
          <TaskPanel
            ref={createPanelRef}
            mode="create"
            workspaceId={workspaceId}
            defaultProjectId={projectId}
            defaultEpicId={project?.epicId}
            currentUserId={user.userId}
            columns={columns}
            labels={labels}
            members={members}
            sprints={sprints}
            getToken={getToken}
            doRefresh={refresh}
            onCreate={handleTaskCreated}
            onCancel={() => setPanelMode(null)}
          />
        </RightPanel>
      )}
      {panelMode && panelMode !== 'create' && selectedTask && (
        <RightPanel
          title="Task"
          onClose={() => setPanelMode(null)}
          onEdit={() => viewPanelRef.current?.startEdit()}
          onDelete={() => handleTaskDelete(selectedTask)}
        >
          <TaskPanel
            ref={viewPanelRef}
            mode="view"
            task={selectedTask}
            workspaceId={workspaceId}
            columns={columns}
            labels={labels}
            members={members}
            sprints={sprints}
            getToken={getToken}
            doRefresh={refresh}
            onUpdate={handleTaskUpdate}
          />
        </RightPanel>
      )}

      {/* Members tab */}
      {activeTab === 'members' && (
        <>
          <h2 style={{ margin: '0 0 8px' }}>Project Members</h2>

          {projectMembers.length === 0 ? (
            <p className="muted">No members assigned to this project yet.</p>
          ) : (
            <ul className="card-list">
              {projectMembers.map(pm => (
                <li key={pm.userId} className="card">
                  <div className="toolbar--split">
                    <div>
                      <strong>{pm.displayName}</strong>
                      <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>{pm.email}</span>
                      <span className="badge" style={{ marginLeft: 8 }}>{Math.round(pm.capacityFraction * 100)}% capacity</span>
                    </div>
                    <button
                      type="button"
                      className="icon-btn icon-btn-danger"
                      title="Remove from project"
                      onClick={() => handleRemoveMember(pm.userId)}
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {members.length > 0 && (
            <form onSubmit={handleAddMember} className="form-card" style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Add member</h3>
              <select value={addMemberId} onChange={e => setAddMemberId(e.target.value)} required>
                <option value="">Select account member…</option>
                {members
                  .filter(m => !projectMembers.some(pm => pm.userId === m.userId))
                  .map(m => (
                    <option key={m.userId} value={m.userId}>{m.displayName}</option>
                  ))}
              </select>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                Capacity
                <input
                  type="number"
                  min={0.1}
                  max={1.0}
                  step={0.1}
                  value={addMemberCapacity}
                  onChange={e => setAddMemberCapacity(Number(e.target.value))}
                  style={{ width: 80 }}
                />
              </label>
              <div className="form-actions">
                <button type="submit" disabled={addingMember || !addMemberId}>
                  {addingMember ? 'Adding…' : 'Add to project'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </main>
  );
}
